/**
 * PersonalPrepFieldsSettingsModal Component
 *
 * Modal for editing personal prep fields configuration from the settings dashboard.
 * This is the SETTINGS modal (saves directly to global settings), NOT the workflow modal
 * (which handles session drafts and "apply to future" logic).
 *
 * Key behaviors:
 * - Manual Save/Discard pattern
 * - Dirty-close blocking: when dirty, backdrop/X/Esc are blocked
 * - SortableList for drag-and-drop reordering
 * - Nested modals for Add/Edit field and Delete confirmation
 * - Validation: errors block save, warnings (duplicate names) do not
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Chip,
  Card,
  CardBody,
  SortableList,
  DragHandle,
  EmptyState,
  useToastNotification,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { generateUUID } from '@/utils';
import type { PreTradingSettings } from '@/types/schema/PreTradingSettings';
import type { DropdownOption } from '@/types/shared';

// ============================================================================
// Types
// ============================================================================

/**
 * Personal prep field type derived from schema
 */
export type PersonalPrepField = PreTradingSettings['personalPrepFields'][number];

/**
 * Blocking validation errors (prevent save)
 */
interface FieldValidationErrors {
  name?: string;
  options?: string;
}

/**
 * Non-blocking warnings (shown but don't prevent save)
 */
interface FieldValidationWarnings {
  name?: string;
}

/**
 * Field type display configuration
 */
const FIELD_TYPE_CONFIG: Record<
  PersonalPrepField['type'],
  { label: string; color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' }
> = {
  checkbox: { label: 'Checkbox', color: 'success' },
  text: { label: 'Text', color: 'primary' },
  number: { label: 'Number', color: 'secondary' },
  dropdown: { label: 'Dropdown', color: 'warning' },
};

const FIELD_TYPE_OPTIONS: Array<{ key: PersonalPrepField['type']; label: string }> = [
  { key: 'checkbox', label: 'Checkbox' },
  { key: 'text', label: 'Text' },
  { key: 'number', label: 'Number' },
  { key: 'dropdown', label: 'Dropdown' },
];

export interface PersonalPrepFieldsSettingsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Async callback when fields are saved - should persist to settings */
  onSave: (updatedFields: PersonalPrepField[]) => Promise<void>;
  /** Initial fields from current settings */
  initialFields: PersonalPrepField[];
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Validate a single field
 * Returns separate errors (blocking) and warnings (non-blocking)
 */
function validateField(
  field: PersonalPrepField,
  allFields: PersonalPrepField[]
): { errors: FieldValidationErrors; warnings: FieldValidationWarnings } {
  const errors: FieldValidationErrors = {};
  const warnings: FieldValidationWarnings = {};
  const trimmedName = field.name.trim();

  // Name required (blocking error)
  if (!trimmedName) {
    errors.name = 'Name is required';
  } else {
    // Check for duplicate names (warning only, not blocking)
    const isDuplicate = allFields.some(
      (f) =>
        f.id !== field.id &&
        f.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      warnings.name = 'Duplicate field name';
    }
  }

  // Dropdown must have at least one option (blocking error)
  if (field.type === 'dropdown') {
    if (!field.options || field.options.length === 0) {
      errors.options = 'At least one option is required';
    }
  }

  return { errors, warnings };
}

/**
 * Check if any field has blocking errors (errors only, NOT warnings)
 */
function hasBlockingErrors(
  fields: PersonalPrepField[],
  validationErrors: Record<string, FieldValidationErrors>
): boolean {
  return fields.some((field) => {
    const fieldErrors = validationErrors[field.id];
    return fieldErrors && Object.keys(fieldErrors).length > 0;
  });
}

// ============================================================================
// Component
// ============================================================================

export const PersonalPrepFieldsSettingsModal: React.FC<PersonalPrepFieldsSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialFields,
}) => {
  const { showToast } = useToastNotification();

  // Local editing state
  const [localFields, setLocalFields] = useState<PersonalPrepField[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, FieldValidationErrors>>({});
  const [validationWarnings, setValidationWarnings] = useState<Record<string, FieldValidationWarnings>>({});

  // Baseline for dirty tracking (stores JSON string)
  const baselineKeyRef = useRef<string>('');

  // Edit modal state
  const [editingFieldId, setEditingFieldId] = useState<string | 'new' | null>(null);
  const [editingDraft, setEditingDraft] = useState<PersonalPrepField | null>(null);
  const [editModalErrors, setEditModalErrors] = useState<FieldValidationErrors>({});
  const [editModalWarnings, setEditModalWarnings] = useState<FieldValidationWarnings>({});
  const [newOptionValue, setNewOptionValue] = useState('');

  // Delete confirmation state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Track previous isOpen state to detect open transition (false → true)
  const wasOpenRef = useRef(false);

  // Compute dirty state using stable JSON comparison
  const localFieldsKey = useMemo(() => JSON.stringify(localFields), [localFields]);
  const isDirty = localFieldsKey !== baselineKeyRef.current;

  // Check if save should be disabled (has blocking errors)
  const hasSaveBlockingErrors = hasBlockingErrors(localFields, validationErrors);

  // Initialize local state from initialFields when modal opens
  // Changes to initialFields while modal is open are ignored (only re-sync on open transition)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Deep copy fields with options
      const fieldsCopy: PersonalPrepField[] = initialFields.map((f) => {
        const field: PersonalPrepField = {
          id: f.id,
          name: f.name,
          type: f.type,
        };
        if (f.description !== undefined) {
          field.description = f.description;
        }
        if (f.options) {
          field.options = f.options.map((o) => ({ ...o }));
        }
        return field;
      });

      // Set local fields and baseline
      setLocalFields(fieldsCopy);
      baselineKeyRef.current = JSON.stringify(fieldsCopy);

      // Reset ALL state to prevent stale UI
      setValidationErrors({});
      setValidationWarnings({});
      setEditingFieldId(null);
      setEditingDraft(null);
      setEditModalErrors({});
      setEditModalWarnings({});
      setNewOptionValue('');
      setDeleteConfirmId(null);
      setIsSaving(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialFields]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validateAllFields = useCallback((fieldsToValidate: PersonalPrepField[]) => {
    const errors: Record<string, FieldValidationErrors> = {};
    const warnings: Record<string, FieldValidationWarnings> = {};

    fieldsToValidate.forEach((field) => {
      const result = validateField(field, fieldsToValidate);
      if (Object.keys(result.errors).length > 0) {
        errors[field.id] = result.errors;
      }
      if (Object.keys(result.warnings).length > 0) {
        warnings[field.id] = result.warnings;
      }
    });

    setValidationErrors(errors);
    setValidationWarnings(warnings);
    return errors;
  }, []);

  // ============================================================================
  // Field CRUD Handlers
  // ============================================================================

  const handleOpenAddModal = useCallback(() => {
    const newField: PersonalPrepField = {
      id: generateUUID(),
      name: '',
      type: 'checkbox',
      // description and options left undefined initially
    };
    setEditingFieldId('new');
    setEditingDraft(newField);
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, []);

  const handleOpenEditModal = useCallback((field: PersonalPrepField) => {
    setEditingFieldId(field.id);
    // Deep copy including options
    setEditingDraft({
      ...field,
      options: field.options ? field.options.map((o) => ({ ...o })) : [],
    });
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, []);

  const handleEditDraftChange = useCallback(
    (key: keyof PersonalPrepField, value: string | DropdownOption[]) => {
      setEditingDraft((prev) => {
        if (!prev) return prev;

        let updated: PersonalPrepField;

        // Handle type change - clear options when switching away from dropdown
        if (key === 'type' && value !== 'dropdown') {
          updated = { ...prev, [key]: value as PersonalPrepField['type'], options: [] };
          setNewOptionValue(''); // Clear pending option input
        } else {
          updated = { ...prev, [key]: value };
        }

        // Validate draft
        const otherFields = editingFieldId === 'new'
          ? localFields
          : localFields.filter((f) => f.id !== editingFieldId);
        const result = validateField(updated, [...otherFields, updated]);
        setEditModalErrors(result.errors);
        setEditModalWarnings(result.warnings);

        return updated;
      });
    },
    [editingFieldId, localFields]
  );

  const handleAddOption = useCallback(() => {
    const trimmed = newOptionValue.trim();
    if (!trimmed || !editingDraft) return;

    const currentOptions = editingDraft.options ?? [];
    // Check for duplicate labels (case-insensitive)
    if (currentOptions.some((o) => o.label.toLowerCase() === trimmed.toLowerCase())) {
      return; // Don't add duplicates
    }

    const newOption: DropdownOption = { id: generateUUID(), label: trimmed };
    handleEditDraftChange('options', [...currentOptions, newOption]);
    setNewOptionValue('');
  }, [newOptionValue, editingDraft, handleEditDraftChange]);

  const handleRemoveOption = useCallback((optionId: string) => {
    if (!editingDraft) return;
    const currentOptions = editingDraft.options ?? [];
    handleEditDraftChange('options', currentOptions.filter((o) => o.id !== optionId));
  }, [editingDraft, handleEditDraftChange]);

  const handleSaveEditModal = useCallback(() => {
    if (!editingDraft) return;

    // Validate before saving
    const otherFields = editingFieldId === 'new'
      ? localFields
      : localFields.filter((f) => f.id !== editingFieldId);
    const result = validateField(editingDraft, [...otherFields, editingDraft]);

    if (Object.keys(result.errors).length > 0) {
      setEditModalErrors(result.errors);
      setEditModalWarnings(result.warnings);
      return;
    }

    // Normalize the field before saving:
    // - Empty description → undefined (prevents false "settings changed" detection)
    // - Empty options array → undefined (for non-dropdown types)
    const normalizedField: PersonalPrepField = {
      id: editingDraft.id,
      name: editingDraft.name.trim(),
      type: editingDraft.type,
    };
    if (editingDraft.description?.trim()) {
      normalizedField.description = editingDraft.description.trim();
    }
    if (editingDraft.type === 'dropdown' && editingDraft.options && editingDraft.options.length > 0) {
      normalizedField.options = editingDraft.options;
    }

    // Update local fields
    let updated: PersonalPrepField[];
    if (editingFieldId === 'new') {
      updated = [...localFields, normalizedField];
    } else {
      updated = localFields.map((f) =>
        f.id === editingFieldId ? normalizedField : f
      );
    }

    setLocalFields(updated);
    validateAllFields(updated);

    // Close modal
    setEditingFieldId(null);
    setEditingDraft(null);
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, [editingDraft, editingFieldId, localFields, validateAllFields]);

  const handleCancelEditModal = useCallback(() => {
    setEditingFieldId(null);
    setEditingDraft(null);
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, []);

  const handleDeleteClick = useCallback((id: string) => {
    setDeleteConfirmId(id);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteConfirmId) return;

    const updated = localFields.filter((f) => f.id !== deleteConfirmId);
    setLocalFields(updated);

    // Clear validation for deleted field
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[deleteConfirmId];
      return next;
    });
    setValidationWarnings((prev) => {
      const next = { ...prev };
      delete next[deleteConfirmId];
      return next;
    });

    // Re-validate remaining fields (duplicate warnings may clear)
    validateAllFields(updated);

    setDeleteConfirmId(null);
  }, [deleteConfirmId, localFields, validateAllFields]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmId(null);
  }, []);

  const handleReorder = useCallback(
    (reorderedFields: PersonalPrepField[]) => {
      setLocalFields(reorderedFields);
      validateAllFields(reorderedFields);
    },
    [validateAllFields]
  );

  // ============================================================================
  // Save/Discard Handlers
  // ============================================================================

  const handleSave = useCallback(async () => {
    // Validate all fields - block on errors only, not warnings
    const currentErrors = validateAllFields(localFields);
    if (hasBlockingErrors(localFields, currentErrors)) {
      showToast({
        title: 'Validation errors',
        description: 'Please fix the errors before saving.',
        color: 'warning',
      });
      return;
    }

    setIsSaving(true);

    try {
      await onSave(localFields);

      // On successful save: update baseline (clears dirty state), then close
      baselineKeyRef.current = JSON.stringify(localFields);
      onClose();
    } catch (err) {
      // On save failure: keep modal open, show error toast, do NOT reset baseline
      console.error('Save error:', err);
      showToast({
        title: 'Save failed',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [localFields, validateAllFields, onSave, onClose, showToast]);

  const handleDiscard = useCallback(() => {
    // Reset localFields to baseline (clears dirty), then close modal
    const baseline = JSON.parse(baselineKeyRef.current) as PersonalPrepField[];
    setLocalFields(baseline);
    setValidationErrors({});
    setValidationWarnings({});
    onClose();
  }, [onClose]);

  // ============================================================================
  // Field Row Renderer
  // ============================================================================

  const renderFieldRow = useCallback(
    (field: PersonalPrepField, dragHandleProps: DragHandleProps) => {
      const typeConfig = FIELD_TYPE_CONFIG[field.type];
      const fieldErrors = validationErrors[field.id];
      const fieldWarnings = validationWarnings[field.id];
      const hasError = fieldErrors && Object.keys(fieldErrors).length > 0;
      const hasWarning = fieldWarnings && Object.keys(fieldWarnings).length > 0;

      return (
        <Card
          shadow="sm"
          radius="sm"
          className={`
            ${dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}
            ${hasError ? 'ring-1 ring-danger-300' : hasWarning ? 'ring-1 ring-warning-300' : ''}
          `}
        >
          <CardBody className="p-3">
            <div className="flex items-center gap-3">
            <DragHandle dragHandleProps={dragHandleProps} />

            {/* Tappable content area */}
            <button
              type="button"
              className="flex-1 text-left min-w-0"
              onClick={() => handleOpenEditModal(field)}
            >
              <div className="flex items-center gap-2">
                <span className={`font-medium truncate ${hasError ? 'text-danger' : ''}`}>
                  {field.name || 'Unnamed field'}
                </span>
                {hasError && <span className="text-danger text-xs">*</span>}
                {hasWarning && !hasError && <span className="text-warning text-xs">!</span>}
              </div>
              {field.description && (
                <div className="text-sm text-default-500 truncate">
                  {field.description}
                </div>
              )}
            </button>

            <Chip size="sm" color={typeConfig.color} variant="flat">
              {typeConfig.label}
            </Chip>

            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => handleOpenEditModal(field)}
              aria-label={`Edit ${field.name || 'field'}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>

            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => handleDeleteClick(field.id)}
              aria-label={`Delete ${field.name || 'field'}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            </div>
          </CardBody>
        </Card>
      );
    },
    [validationErrors, validationWarnings, handleOpenEditModal, handleDeleteClick]
  );

  // Get field name for delete confirmation
  const deleteFieldName = deleteConfirmId
    ? localFields.find((f) => f.id === deleteConfirmId)?.name || 'this field'
    : '';

  return (
    <>
      {/* Main Config Modal */}
      <Modal
        isOpen={isOpen && editingFieldId === null && deleteConfirmId === null}
        onClose={onClose}
        placement="top"
        scrollBehavior="inside"
        size="2xl"
        isDismissable={!isDirty}
        hideCloseButton={isDirty}
        isKeyboardDismissDisabled={isDirty}
      >
        <ModalContent>
          <ModalHeader>
            <span className="text-lg font-semibold">Personal Prep Fields</span>
          </ModalHeader>
          <ModalBody className="gap-4">
            {/* Fields List */}
            {localFields.length > 0 ? (
              <SortableList
                items={localFields}
                onReorder={handleReorder}
                renderItem={renderFieldRow}
                className="space-y-2"
              />
            ) : (
              <EmptyState
                title="No fields configured"
                description="Add fields to customize your daily pre-trading preparation."
              />
            )}

            {/* Add Field Button */}
            <Button
              variant="flat"
              color="primary"
              startContent={<Plus className="w-4 h-4" />}
              onPress={handleOpenAddModal}
              className="w-full md:w-auto"
            >
              Add Field
            </Button>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={isDirty ? handleDiscard : onClose}
              isDisabled={isSaving}
            >
              {isDirty ? 'Discard' : 'Cancel'}
            </Button>
            <Button
              color="primary"
              onPress={handleSave}
              isDisabled={!isDirty || isSaving || hasSaveBlockingErrors}
              isLoading={isSaving}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit/Add Field Modal */}
      <Modal
        isOpen={editingFieldId !== null}
        onClose={handleCancelEditModal}
        placement="top"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            {editingFieldId === 'new' ? 'Add Field' : 'Edit Field'}
          </ModalHeader>
          <ModalBody className="gap-4">
            {editingDraft && (
              <>
                {/* Field Name */}
                <Input
                  label="Field Name"
                  placeholder="e.g., Morning Routine Complete"
                  value={editingDraft.name}
                  onChange={(e) => handleEditDraftChange('name', e.target.value)}
                  isInvalid={!!editModalErrors.name}
                  errorMessage={editModalErrors.name}
                  description={editModalWarnings.name ? (
                    <span className="text-warning">{editModalWarnings.name}</span>
                  ) : undefined}
                  autoFocus
                  classNames={{ input: 'text-base' }}
                />

                {/* Description (optional) */}
                <Input
                  label="Description (optional)"
                  placeholder="Brief description of this field"
                  value={editingDraft.description || ''}
                  onChange={(e) => handleEditDraftChange('description', e.target.value)}
                  classNames={{ input: 'text-base' }}
                />

                {/* Field Type */}
                <Select
                  label="Field Type"
                  selectedKeys={new Set([editingDraft.type])}
                  onSelectionChange={(keys) => {
                    if (keys !== 'all') {
                      const selected = Array.from(keys)[0] as PersonalPrepField['type'];
                      if (selected) {
                        handleEditDraftChange('type', selected);
                      }
                    }
                  }}
                >
                  {FIELD_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>

                {/* Dropdown Options Editor */}
                {editingDraft.type === 'dropdown' && (
                  <div className="space-y-3">
                    <div className="text-sm font-medium">
                      Dropdown Options
                      {editModalErrors.options && (
                        <span className="text-danger ml-2 font-normal">
                          {editModalErrors.options}
                        </span>
                      )}
                    </div>

                    {/* Existing Options */}
                    {editingDraft.options && editingDraft.options.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {editingDraft.options.map((option) => (
                          <Chip
                            key={option.id}
                            variant="flat"
                            onClose={() => handleRemoveOption(option.id)}
                          >
                            {option.label}
                          </Chip>
                        ))}
                      </div>
                    )}

                    {/* Add New Option */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="New option"
                        value={newOptionValue}
                        onChange={(e) => setNewOptionValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddOption();
                          }
                        }}
                        size="sm"
                        classNames={{ base: 'flex-1' }}
                      />
                      <Button
                        size="sm"
                        variant="flat"
                        color="primary"
                        onPress={handleAddOption}
                        isDisabled={!newOptionValue.trim()}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCancelEditModal}>
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={handleSaveEditModal}
              isDisabled={Object.keys(editModalErrors).length > 0}
            >
              {editingFieldId === 'new' ? 'Add' : 'Save'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmId !== null} onClose={handleCancelDelete}>
        <ModalContent>
          <ModalHeader>Delete Field?</ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to delete <strong>{deleteFieldName}</strong>?
            </p>
            <p className="text-default-500 text-sm mt-2">
              This action cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCancelDelete}>
              Cancel
            </Button>
            <Button color="danger" onPress={handleConfirmDelete}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
